import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClients, getAllTerminals, getMyTerminals, getTerminalSummaries } from '../services/api';
import { FaServer, FaUsers, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaCalendarAlt, FaDatabase, FaInfoCircle } from 'react-icons/fa';
import { getTerminalName } from '../utils/terminalNames';

const StatCard = ({ icon: Icon, label, value, color, bgColor }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500 font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      </div>
      <div className={`w-12 h-12 rounded-lg ${bgColor} flex items-center justify-center`}>
        <Icon className={`w-5 h-5 ${color}`} />
      </div>
    </div>
  </div>
);



const ObservationPanel = ({ assessment, depth }) => {
  if (assessment?.version !== 'observations-v1') {
    return <p role="status" className="text-sm text-amber-800">Interpretación no disponible: actualizar API agronómica.</p>;
  }
  const metric = (id) => assessment.metrics.find(item => item.id === `${id}_${depth}`);
  const humidity = metric('humidity');
  const conductivity = metric('conductivity');
  const temperature = metric('temperature');
  const sensorNotes = assessment.notes.slice(0, 2);
  const interpretationNotes = assessment.notes.slice(2);
  return <>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-2">
      <GaugeMeter label={`Humedad · ${depth} cm`} metric={humidity} min={0} max={100} unit="%" />
      <GaugeMeter label={`Conductividad · ${depth} cm`} metric={conductivity}
        min={0} max={conductivity?.sensor_model ? 20000 : 10000}
        unit={conductivity?.sensor_model ? 'µS/cm' : '(unidad por confirmar)'} />
      <GaugeMeter label={`Temperatura suelo · ${depth} cm`} metric={temperature} min={-40} max={80} unit="°C" />
    </div>
    <details className="group mt-3 border-t border-gray-100 pt-2 text-sm text-gray-600">
      <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 font-semibold text-gray-700 [&::-webkit-details-marker]:hidden">
        <FaInfoCircle className="text-emerald-700" />
        Detalles de medición y criterios
      </summary>
      <div className="space-y-4 pb-3 pt-2 text-sm">
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-gray-50 p-3">
          <span className="flex items-center gap-2 font-semibold text-gray-800"><FaCalendarAlt className="text-emerald-700" />{assessment.recorded_at_label}</span>
          <span className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${assessment.freshness === 'recent' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>{assessment.freshness_label}</span>
        </div>
        <div className="flex gap-3 rounded-lg border border-gray-100 p-3">
          <FaDatabase className="mt-0.5 shrink-0 text-gray-400" />
          <div><p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">Origen del registro</p><p className="mt-0.5 text-gray-700">{assessment.source}</p></div>
        </div>
        <section>
          <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-500">Calidad de las lecturas</h4>
          <div className="grid gap-2 sm:grid-cols-3">
            {[humidity, conductivity, temperature].filter(Boolean).map(item => <div key={item.id} className="rounded-lg border border-gray-100 bg-white p-3">
              <p className="text-xs font-semibold text-gray-800">{item.label}</p>
              <p className="mt-1 flex items-start gap-1.5 text-xs leading-5 text-gray-600"><FaInfoCircle className="mt-1 shrink-0 text-amber-700" />{item.quality_label}</p>
              {item.converted_value != null && <p className="mt-1 text-xs font-semibold text-emerald-800">Equivalencia: {item.converted_value.toFixed(3)} {item.converted_unit}</p>}
            </div>)}
          </div>
        </section>
        {sensorNotes.length > 0 && <section>
          <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-500">Equipo y unidades</h4>
          <ul className="space-y-2">{sensorNotes.map((note, index) => <li key={index} className="rounded-lg bg-emerald-50/70 p-3 leading-5 text-gray-700">{note}</li>)}</ul>
        </section>}
        {interpretationNotes.length > 0 && <section>
          <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-gray-500">Alcance de la interpretación</h4>
          <ul className="space-y-2">{interpretationNotes.map((note, index) => <li key={index} className="rounded-lg bg-gray-50 p-3 leading-5 text-gray-700">{note}</li>)}</ul>
        </section>}
      </div>
    </details>
  </>;
};

const GaugeMeter = ({ label, metric, min, max, unit }) => {
  const value = typeof metric?.value === 'number' && Number.isFinite(metric.value) ? metric.value : null;
  const bounded = value == null ? null : Math.min(max, Math.max(min, value));
  const percent = bounded == null ? 0 : ((bounded - min) / (max - min)) * 100;
  const angle = -90 + percent * 1.8;
  const id = `gauge-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return <div className="bg-white rounded-xl border border-gray-200 p-3 shadow-sm">
    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider text-center">{label}</p>
    <div className="relative h-32 w-full" aria-label={`${label}: ${metric?.display_value || 'Sin datos válidos'}`} role="img">
      <svg viewBox="0 0 220 130" className="w-full h-full" aria-hidden="true">
        <defs><linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor="#22c55e"/><stop offset="55%" stopColor="#facc15"/><stop offset="80%" stopColor="#f97316"/><stop offset="100%" stopColor="#ef4444"/></linearGradient></defs>
        <path d="M 20 110 A 90 90 0 0 1 200 110" fill="none" stroke="#e5e7eb" strokeWidth="16" strokeLinecap="round"/>
        <path d="M 20 110 A 90 90 0 0 1 200 110" fill="none" stroke={`url(#${id})`} strokeWidth="14" strokeLinecap="round"/>
        {value != null && <><line x1="110" y1="110" x2="110" y2="34" stroke="#111827" strokeWidth="3" strokeLinecap="round" transform={`rotate(${angle}, 110, 110)`}/><circle cx="110" cy="110" r="6" fill="#111827"/></>}
        <text x="110" y="85" textAnchor="middle" className={value == null ? 'fill-gray-500' : 'fill-gray-900'} fontSize={value == null ? '15' : '19'} fontWeight="700">{value == null ? 'Sin datos' : `${value.toFixed(1)} ${unit}`}</text>
      </svg>
    </div>
    <div className="flex justify-between text-[11px] text-gray-400"><span>{min} {unit}</span><span>{max} {unit}</span></div>
    {value != null && metric?.converted_value != null && <p className="text-center text-xs font-semibold text-emerald-700 mt-1">{metric.converted_value.toFixed(3)} {metric.converted_unit}</p>}
  </div>;
};

const DashboardPage = ({ token, user }) => {
  const [depth, setDepth] = useState(30);
  const [clients, setClients] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summaries, setSummaries] = useState([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const isAdmin = user && user.role === 1;

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError('');
      try {
        if (isAdmin) {
          const [cRes, tRes] = await Promise.all([getClients(token), getAllTerminals(token)]);
          if (cRes.cod === 200) setClients(cRes.data);
          if (tRes.cod === 200) setTerminals(tRes.data);
        } else {
          const [tRes, sRes] = await Promise.all([getMyTerminals(token), getTerminalSummaries(token, 7)]);
          if (tRes.cod === 200) setTerminals(tRes.data);
          if (sRes.cod === 200) setSummaries(sRes.data || []);
          else throw new Error(sRes.message || 'No se pudieron cargar las lecturas');
        }
      } catch (err) {
        setSummaries([]);
        setError(err.message || 'Error al cargar el dashboard');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [token, isAdmin]);

  const activeTerminals = terminals.filter(t => t.estado).length;
  const inactiveTerminals = terminals.filter(t => !t.estado).length;
  const unassignedTerminals = terminals.filter(t => !t.id_cliente).length;
  const activeClients = clients.filter(c => c.estado === 'activo').length;

  const terminalMap = {};
  terminals.forEach(t => { terminalMap[t.guid] = t; });


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">
          {isAdmin ? 'Resumen general del sistema' : 'Bienvenido al panel de control'}
        </p>
      </div>

      {error && <p role="alert" className="text-sm text-red-700 mb-4">{error}</p>}
      {isAdmin ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard icon={FaServer} label="Terminales Activas" value={activeTerminals} color="text-emerald-600" bgColor="bg-emerald-50" />
            <StatCard icon={FaTimesCircle} label="Terminales Inactivas" value={inactiveTerminals} color="text-red-600" bgColor="bg-red-50" />
            <StatCard icon={FaUsers} label="Clientes Activos" value={activeClients} color="text-blue-600" bgColor="bg-blue-50" />
            <StatCard icon={FaExclamationTriangle} label="Sin Asignar" value={unassignedTerminals} color="text-amber-600" bgColor="bg-amber-50" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-4">Últimos Clientes</h3>
              <div className="space-y-3">
                {clients.slice(0, 5).map(c => (
                  <div key={c.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="text-sm font-medium text-gray-800">{c.nombre}</p>
                      <p className="text-xs text-gray-400">{c.rut}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                      c.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {c.estado}
                    </span>
                  </div>
                ))}
                {clients.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No hay clientes registrados</p>}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-4">Últimas Terminales</h3>
              <div className="space-y-3">
                {terminals.slice(0, 5).map(t => (
                  <div key={t.guid} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <p className="text-sm font-mono font-medium text-gray-800">{t.guid}</p>
                      <p className="text-xs text-gray-400">{t.ubicacion || 'Sin ubicación'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {t.cliente_nombre && <span className="text-xs text-gray-500">{t.cliente_nombre}</span>}
                      {t.estado ? (
                        <FaCheckCircle className="w-4 h-4 text-emerald-500" />
                      ) : (
                        <FaTimesCircle className="w-4 h-4 text-red-500" />
                      )}
                    </div>
                  </div>
                ))}
                {terminals.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No hay terminales registradas</p>}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
              <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider">Panel Agronómico Visual</h3>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex rounded-lg border border-gray-300 bg-gray-50 p-1" role="group" aria-label="Profundidad de lectura">
                  {[30, 60].map(cm => <button key={cm} aria-pressed={depth === cm} onClick={() => setDepth(cm)} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${depth === cm ? 'bg-emerald-600 text-white' : 'text-gray-600 hover:bg-white'}`}>{cm} cm</button>)}
                </div>
                <button onClick={() => navigate('/terminales')} className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-xs font-semibold">Mis Terminales</button>
                <button onClick={() => navigate('/graficos')} className="px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-xs font-semibold">Ver Gráficos</button>
              </div>
            </div>
            <div className="space-y-4">
              {summaries.map((s) => {
                const term = terminalMap[s.guid] || {};
                return (
                  <div key={s.guid} className="border border-gray-100 rounded-xl p-4 bg-gradient-to-br from-white via-emerald-50/30 to-white">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900">{getTerminalName(s)}</p>
                        <p className="text-[11px] text-gray-500 font-mono">GUID: {s.guid}</p>
                      </div>
                      {term.tipo_cultivo || term.tipo_suelo ? (
                        <span className="text-[10px] text-gray-400 bg-gray-50 border border-gray-200 rounded px-2 py-0.5">{term.tipo_cultivo || '—'} / {term.tipo_suelo ? ({ FrancoArenoso: 'F. Arenoso', Franco: 'Franco', Arcilloso: 'Arcilloso' }[term.tipo_suelo] || term.tipo_suelo) : '—'}</span>
                      ) : null}
                    </div>
                    <ObservationPanel assessment={s.agronomy} depth={depth} />
                  </div>
                );
              })}
              {!error && summaries.length === 0 && <p className="text-sm text-gray-500 text-center py-4">Sin lecturas en los últimos 7 días</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
